class Promotion {
  static BUY_X_GET_Y = "promotion_buy_x_get_y";
  static BUY_N_X_GET_N_Y = "promotion_buy_n_quantity_of_x_get_n_quantity_of_y";
  static AMOUNT_SPENT = "promotion_amount_spent_get_gift";

  static ACTIVE = "active";
  static NOT_ACTIVE = "not-active";
  static USED = "used";
  static NOT_USED = "not-used";

  static PROMOTION_HANDLE = "_promotion";
  static IS_PROMOTION = "_is_promotion";

  constructor(data) {
    this.type = data.type;
    this.handle = data.handle;
    this.promo = {
      object_name: data.promo.object_name,
      products_to_buy: data.promo.products_to_buy || [],
      products_to_buy_types: Array.isArray(data.promo.products_to_buy_types)
        ? data.promo.products_to_buy_types
        : data.promo.products_to_buy_types
          ? [data.promo.products_to_buy_types]
          : [],
      product_quantity: data.promo.product_quantity,
      products_to_get: data.promo.products_to_get,
      custom_thresholds: data.promo.custom_thresholds,
      allowed_countries: data.promo.allowed_countries || [],
    };
    this.available_product_variants = data.available_product_variants || {};
  }

  hasOneVariant() {
    return Object.keys(this.available_product_variants).length > 0;
  }

  /**
   * Check if the promotion has a single variant available, if so return it
   * @returns {string?}
   */
  getSingleVariant() {
    if (
      this.promo.products_to_get?.length === 1 &&
      this.available_product_variants[this.promo.products_to_get[0]]?.length ===
        1
    ) {
      return this.available_product_variants[this.promo.products_to_get[0]][0];
    }

    return null;
  }

  getThreshold(country) {
    return this.promo.custom_thresholds[country] || null;
  }

  /**
   * Check if the promotion is allowed in the given country
   * If allowed_countries is empty, the promotion is allowed everywhere
   * @param {string} country - ISO country code
   * @returns {boolean}
   */
  isAllowedInCountry(country) {
    if (
      !this.promo.allowed_countries ||
      this.promo.allowed_countries.length === 0
    )
      return true;
    return this.promo.allowed_countries.includes(country);
  }
}

class PromotionsUtils {
  static async getCart() {
    try {
      const response = await fetch(`${window.theme.routes.cart_url}.js`, {
        method: "GET",
      });
      return await response.json();
    } catch (error) {
      console.error("Error:", error);
    }
  }

  /**
   * Add a product variant to the cart
   * @param {Promotion} promotion
   * @param {String} variant
   * @param {Number} quantity
   * @returns {Object} cart/add response
   */
  static async addToCart(promotion, variant, quantity = 1) {
    const formData = {
      items: [
        {
          id: variant.split("gid://shopify/ProductVariant/")[1],
          quantity: quantity,
          properties: {
            [Promotion.PROMOTION_HANDLE]: promotion.handle,
            [Promotion.IS_PROMOTION]: true,
          },
        },
      ],
    };

    try {
      return await fetch(`${window.theme.routes.cart_add_url}.js`, {
        body: JSON.stringify(formData),
        headers: { "Content-Type": "application/json" },
        method: "POST",
      });
    } catch (error) {
      console.error("Error adding to cart: ", error);
    }
  }

  static getChangePromise(index, quantity, properties) {
    return fetch("/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        line: index,
        quantity: quantity,
        properties: properties,
      }),
    });
  }

  static getCartPromise(attributes) {
    return fetch("/cart/update.js", {
      method: "POST",
      mode: "cors",
      cache: "no-cache",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      redirect: "follow",
      referrerPolicy: "no-referrer",
      body: JSON.stringify({ attributes: attributes }),
    });
  }
}

if (!customElements.get("promotions-handler")) {
  class PromotionsHandler extends HTMLElement {
    constructor() {
      super();
      this.cart = {};
      this.queue = [];
    }

    get activePromos() {
      return JSON.parse(
        this.querySelector("[data-active-promotions]").innerHTML,
      ).map((promo) => new Promotion(promo));
    }

    /**
     * Subscribe to the cart update event
     */
    connectedCallback() {
      this.onCartChange = this.handleCartUpdate.bind(this);
      document.addEventListener("theme:cart:change", this.onCartChange);
    }

    disconnectedCallback() {
      document.removeEventListener("theme:cart:change", this.onCartChange);
    }

    /**
     * - Update the stored cart object
     * - Handle the update based on the active promotions, skip if there's no active promotion
     */
    async handleCartUpdate() {
      this.queue = [];
      const country = this.dataset.country || "IT";

      for (const promotion of this.activePromos) {
        this.cart = await PromotionsUtils.getCart();
        if (!this.cart) continue;

        if (!promotion.isAllowedInCountry(country)) {
          const hasPromoItems = this.cart.items?.some(
            (item) =>
              item.properties?.[Promotion.PROMOTION_HANDLE] ===
              promotion.handle,
          );
          if (hasPromoItems) {
            await this.disablePromotion(promotion);
            this.dispatchEvent(
              new CustomEvent("theme:cart:refresh", {
                bubbles: true,
                detail: {},
              }),
            );
          }
          continue;
        }

        if (!this.cart.items || this.cart.items.length === 0) {
          const attributes = this.cart.attributes || {};
          attributes[promotion.handle] = false;
          await PromotionsUtils.getCartPromise(attributes);
          this.queue.push({
            payload: {
              products_to_get: promotion.promo.products_to_get || [],
              current_items: [],
              triggered_promotion: promotion.handle,
              "promotion-type": promotion.type,
              status: Promotion.NOT_USED,
            },
          });
          continue;
        }

        switch (promotion.type) {
          case Promotion.AMOUNT_SPENT:
            await this.handleAmountSpentPromotion(promotion);
            break;
          case Promotion.BUY_X_GET_Y:
            await this.handleBuyXGetYPromotion(promotion);
            break;
          case Promotion.BUY_N_X_GET_N_Y:
            await this.handleBuyNXGetNYPromotion(promotion);
            break;
          default:
            break;
        }
      }
    }

    /**
     * Handle the "amount_spent_get_gift_promotion" case
     * @param {Promotion} promotion
     */
    async handleAmountSpentPromotion(promotion) {
      const country = this.dataset.country || "IT";
      if (promotion.getThreshold(country) == null) return;
      let shouldFire =
        promotion.getThreshold(country) != null &&
        this.cart.total_price / 100 >= promotion.getThreshold(country);

      return await this.processPromotionProducts(promotion, shouldFire);
    }

    /**
     * Handle the "buy_x_get_y_promotion" case
     * @param {Promotion} promotion
     */
    async handleBuyXGetYPromotion(promotion) {
      let qty = 0;
      this.cart.items?.forEach((cartItem) => {
        promotion.promo.products_to_buy =
          typeof promotion.promo.products_to_buy === "string"
            ? [promotion.promo.products_to_buy]
            : promotion.promo.products_to_buy;
        promotion.promo.products_to_buy.forEach((prodToBuy) => {
          prodToBuy.includes("gid://shopify/Product/" + cartItem.product_id) &&
            (qty += cartItem.quantity);
        });
      });

      let shouldFire = qty >= promotion.promo.product_quantity;

      return await this.processPromotionProducts(promotion, shouldFire);
    }

    /**
     * Handle the "buy_n_x_get_n_y_promotion" case
     * When user adds a product with product_type === products_to_buy_type,
     * automatically add products_to_get with matching quantity
     * @param {Promotion} promotion
     */
    async handleBuyNXGetNYPromotion(promotion) {
      // Calculate the total quantity of products matching products_to_buy_types
      let buyQty = 0;
      this.cart.items?.forEach((cartItem) => {
        if (promotion.promo.products_to_buy_types.some((type) => type.toLowerCase() === cartItem.product_type?.toLowerCase())) {
          buyQty += cartItem.quantity;
        }
      });

      // Calculate current quantity of promotion products in cart
      let currentPromoQty = 0;
      let promoItem = null;
      this.cart.items?.forEach((item, index) => {
        if (
          item.properties?.[Promotion.PROMOTION_HANDLE] === promotion.handle
        ) {
          currentPromoQty += item.quantity;
          promoItem = { ...item, lineIndex: index + 1 };
        }
      });

      // If no products to buy, remove promotion products if any
      if (buyQty === 0 && currentPromoQty > 0) {
        await this.disablePromotion(promotion);
        // Mark promotion as used so products_to_get won't be re-added
        const attributes = this.cart.attributes || {};
        attributes[promotion.handle] = Promotion.USED;
        await PromotionsUtils.getCartPromise(attributes);
        this.queue.push({
          payload: {
            products_to_get: promotion.promo.products_to_get || [],
            current_items: [],
            triggered_promotion: promotion.handle,
            "promotion-type": promotion.type,
            status: Promotion.USED,
          },
        });
        this.dispatchEvent(
          new CustomEvent("theme:cart:refresh", { bubbles: true, detail: {} }),
        );
        return;
      }

      // If quantities match, nothing to do
      if (buyQty === currentPromoQty) {
        return;
      }

      // If promotion was already used, don't re-add products_to_get
      const isPromotionUsed =
        this.cart.attributes?.[promotion.handle] === Promotion.USED;
      if (isPromotionUsed) return;

      // If promo was previously triggered but products_to_get were removed by the user, mark as USED
      const wasPromotionTriggered =
        this.cart.attributes?.[promotion.handle] === true ||
        this.cart.attributes?.[promotion.handle] === "true";
      if (buyQty > 0 && currentPromoQty === 0 && wasPromotionTriggered) {
        const attributes = this.cart.attributes || {};
        attributes[promotion.handle] = Promotion.USED;
        await PromotionsUtils.getCartPromise(attributes);
        this.queue.push({
          payload: {
            products_to_get: promotion.promo.products_to_get || [],
            current_items: [],
            triggered_promotion: promotion.handle,
            "promotion-type": promotion.type,
            status: Promotion.USED,
          },
        });
        return;
      }

      // If we need to add or update promotion products
      if (buyQty > 0) {
        const singleVariant = promotion.getSingleVariant();

        if (singleVariant) {
          if (currentPromoQty === 0) {
            // No promo items yet, add with correct quantity
            await PromotionsUtils.addToCart(promotion, singleVariant, buyQty);
            await this.updateTriggerItems(promotion);
          } else if (promoItem) {
            // Update existing promo item quantity to match buyQty
            await PromotionsUtils.getChangePromise(
              promoItem.lineIndex,
              buyQty,
              promoItem.properties,
            );
          }
          this.dispatchEvent(
            new CustomEvent("theme:cart:refresh", {
              bubbles: true,
              detail: {},
            }),
          );
        }
      }
    }

    /**
     * Check if there are available variants, if not return
     *
     * If there are variants available, this is the logic to process the promotion products:
     *- if there's a single variant,                    `add to cart`
     *- if there are multiple variants,                 `publish event`
     *- if a product has a single variant,              `add to cart`
     *- if a product has multiple available variants,   `publish event`
     * @param {Promotion} promotion
     * @param {Boolean} shouldFire
     */
    async processPromotionProducts(promotion, shouldFire = false) {
      if (!promotion.hasOneVariant()) return;

      const currentItems = [];

      this.cart.items?.forEach((item) => {
        if (item.properties[Promotion.PROMOTION_HANDLE] === promotion.handle) {
          currentItems.push(item);
        }
      });

      const isPromotionActive =
        this.cart.attributes[promotion.handle] === true ||
        this.cart.attributes[promotion.handle] === "true";

      let status = isPromotionActive
        ? currentItems.length > 0
          ? Promotion.USED
          : Promotion.NOT_USED
        : shouldFire
          ? Promotion.ACTIVE
          : Promotion.NOT_ACTIVE;

      if (shouldFire) {
        const singleVariant = promotion.getSingleVariant();
        if (
          singleVariant &&
          status !== Promotion.USED &&
          status !== Promotion.NOT_USED
        ) {
          await this.handleAddToCart(promotion, singleVariant);
          status = Promotion.USED;
        }
      } else if (status === Promotion.USED) {
        await this.disablePromotion(promotion);
        status = Promotion.NOT_ACTIVE;
        this.dispatchEvent(
          new CustomEvent("theme:cart:refresh", { bubbles: true, detail: {} }),
        );
      }

      this.queue.push({
        payload: {
          products_to_get: promotion.promo.products_to_get || [],
          current_items: currentItems,
          triggered_promotion: promotion.handle,
          "promotion-type": promotion.type,
          status: status,
        },
      });
    }

    /**
     *
     * @param {Promotion} promotion
     */
    async updateTriggerItems(promotion) {
      const attributes = this.cart.attributes;
      attributes[promotion.handle] = true;

      await PromotionsUtils.getCartPromise(attributes);
    }

    async handleAddToCart(promotion, variant) {
      await PromotionsUtils.addToCart(promotion, variant);
      await this.updateTriggerItems(promotion);
      this.dispatchEvent(
        new CustomEvent("theme:cart:refresh", { bubbles: true, detail: {} }),
      );
    }

    /**
     *
     * @param {Promotion} promotion
     */
    async disablePromotion(promotion) {
      const updates = [];
      let removeItemIndex = 1;
      for (const item of this.cart.items) {
        const properties = item.properties || {};
        const isPromotionItem = properties._promotion === promotion.handle;

        if (isPromotionItem) {
          await PromotionsUtils.getChangePromise(
            removeItemIndex,
            0,
            properties,
          );
        } else {
          // ensures the index is shifted after removing a line item
          removeItemIndex++;
        }
      }

      const attributes = this.cart.attributes;
      attributes[promotion.handle] = false;

      updates.push(PromotionsUtils.getCartPromise(attributes));
      await Promise.all(updates);
      // reload the current page
    }
  }

  customElements.define("promotions-handler", PromotionsHandler);
}
